import {
    Color,
    Matrix4,
    Mesh,
    MeshBasicMaterial,
    OrthographicCamera,
    PerspectiveCamera,
    RingGeometry,
    Scene,
    ShaderMaterial,
    Vector3,
    WebGLRenderer
} from "three";
import {Layer} from "features/apartmentViewer/ApartmentViewer/layers/Layer";
import {CanvasService} from "features/apartmentViewer/ApartmentViewer/services/CanvasService";
import {EventManager} from "features/apartmentViewer/ApartmentViewer/services/EventManager";
import {CameraHandler} from "features/apartmentViewer/ApartmentViewer/services/CameraHandler";
import {ApartmentViewerSettings} from "features/apartmentViewer/ApartmentViewer/ApartmentViewerSettings";
import {TimeService} from "features/apartmentViewer/ApartmentViewer/services/TimeService";
import {LayerType} from "features/apartmentViewer/types";

export class ArrowLayer extends Layer {
    private static readonly SPINNER_COLOR: number = 0xffffff;
    private readonly scene: Scene;
    private arrowMaterial: MeshBasicMaterial;
    private mouseArrowObject: Mesh<RingGeometry, ShaderMaterial | MeshBasicMaterial>;
    private loadingSpinnerObject: Mesh<RingGeometry, MeshBasicMaterial>;

    constructor(
        protected canvasService: CanvasService,
        private eventManager: EventManager,
        private renderer: WebGLRenderer,
        private cameraHandler: CameraHandler,
        private settings: ApartmentViewerSettings,
        private timeService: TimeService,
    ) {
        super(canvasService, LayerType.NoDepth);
        this.scene = new Scene();
        this.init();
    }

    async init() {

        this.alpha = .6;
        await this.createArrowMesh();
        this.createLoaderMesh();
    }

    async createArrowMesh() {
        //let arrowMaterial = "data:image/png;base64,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";
        /*
        let arrowMaterial = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADgAAAA4CAYAAACohjseAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAPwSURBVHgB5VtLUttAEG35AHCB+AKYfYB9INkbDkDBPsABQrLHOQAmBzDZB5J9nOwxB8AcIPgCpJ/VQwnhz5vRSJaTVzWIUo2m9dQ93T0940Qi4/HxcUUv69pa2tbsuqrtVa7rvbahtpG2vrZBkiQ/JTISiQAjtaXtQFJCKxIGR/ZKyfYkAgoRNGKHkhILJTUN0C7IdpTsUAIRRLBkYpPQ0dYLIepNUMnt6OWTvJxTZQPkOr6mSxM0rZ1IqrVFoqvtTImOmM4UQSXX1MuFpA6kDoA2dxmTnUvQyH2V6k1yHiiSMwnWmJzDXJJTCS4BOYeZJCcSNIfyQ4qRQ6byTdutpPHswTkGG79p429qeyfFZA20tSc5nmkEP0q4t4QbR8zq+zykMlsmc0/C0FWZH/I3kwmCIOCz+AOEjopkHSYfmj2WMKL7Kv86eyOZMLjvvINZIC51JSL0XaBNxF2fTAkfdztrqo1cB3w1H3IYsB2bHGBjbks6l1lAQYfPxnH/mPZ+CQ862BZBgFVBe6+dFrMaPBYeeLh0coDJ2DeZDNxCYIwswS3hcVYFOQeVhTBw5vHIgYWilKB5TtYEemXMuXkwmWzocQvwJw3uCI+OLA5HHn3HcdwRZM2zV6Vp5mGy2fVgC2ba0D8gx8aaC1k8WILg1IIG2TXeUL/gjSwYlgKysXEdBDfIztdSH1yR/dZAcJXs7JU8l4wB2W9sok2ys0/KVDbocAGCbPy7k/rggezXbJAdha1iVQGfd6EJLitogi63qwN83gUEWefBetsqwDrGIQj+ITtvSn3AOsZ7ELwlO9elqg2wufPIh6DPiqNsvCX79UGQzS+bOrkXbqZWXmRN9AYEkfawcSW0ZhkTbL12hMS8YUGTze32rAi0EJhs9iOP0zkXB8+FR0hROBZ8CmPjFYcjCLasmW7qlzyUimEyWe0N3U7wmKCZqU8h6dgmeyXIlPNZPK02soVfpD+/hS9f1LXwC2y493rKRQO0CMGXZTodsxJfcs9qtvlkG87GZ1kEct/LmJM2pi85ELvM3pi0fYaM5Yv4A5O6k8TZPoOnDkkqjvLHTKZtgJ5KbpfGAxBw4VuBs/LlroQnE+cq8zR/c9YWNsyjiKeEJhGLsGOFJdldbgsbyy9oCQf2im5hv9gXdPg/DyE4/NPHSByW/SDQ3JqMDdCWetVFsTigkgyq6GQDvRG/pLws4B3abDgKOU4JN47TD1WbLDzk+/wxkXkIPRCLeYmYdSLlA8SgtW5I8bnokWaX5SNIx9ZoIWIOUQ6lA2a6KAYheBc5lA4HAmL9GNsF0QhmYcUp/LTAkXUH77KAV8YmCgihsofUbhB7D+QvXKpwYYNaJd0AAAAASUVORK5CYII=";

        this.arrowMaterial = new MeshBasicMaterial();
        this.arrowMaterial.transparent = true;
        this.arrowMaterial.side = 2;

        this.mouseArrowObject = await this.assetManager.loadTexturedPlaneModel(arrowMaterial, this.arrowMaterial, void 0, new Vector3(.25,.25,.25))
        */

        const spinnerGeometry = new RingGeometry(0.1, 0.15);
        const spinnerMaterial = new MeshBasicMaterial({
            color: ArrowLayer.SPINNER_COLOR,
            side: 2,
        });

        spinnerGeometry.applyMatrix4(new Matrix4().makeRotationX(-Math.PI / 2).multiply(new Matrix4().makeScale(.75, .75, .75)));

        this.mouseArrowObject = new Mesh(spinnerGeometry, spinnerMaterial);

        this.scene.add(this.mouseArrowObject);

    }

    createLoaderMesh() {
        // Create the loading spinner
        const spinnerGeometry = new RingGeometry(0.1, 0.15, 32, 8, 0, Math.PI * 1.75); // Ring with partial gaps for better visibility
        const spinnerMaterial = new MeshBasicMaterial({
            color: ArrowLayer.SPINNER_COLOR,
            side: 2,
        });

        spinnerGeometry.applyMatrix4(new Matrix4().makeRotationX(-Math.PI / 2).multiply(new Matrix4().makeScale(.75, .75, .75)));

        this.loadingSpinnerObject = new Mesh(spinnerGeometry, spinnerMaterial);
        this.loadingSpinnerObject.material.color = new Color(ArrowLayer.SPINNER_COLOR);

        const spinnerSegmentMaterial = new MeshBasicMaterial({
            color: ArrowLayer.SPINNER_COLOR,
            side: 2,
        });

        const spinnerSegment = new Mesh(spinnerGeometry, spinnerSegmentMaterial);
        spinnerSegment.rotation.z = Math.PI; // Rotate to offset the segments slightly

        this.scene.add(this.loadingSpinnerObject);
        this.scene.add(spinnerSegment);
    }

    updateArrowVisibility(camera: PerspectiveCamera | OrthographicCamera, hitPoint: Vector3) {
        if (this.mouseArrowObject) {
            this.mouseArrowObject.visible = true;//!isFloor;

            if(this.settings.isMobile) {
                this.hideAllMeshes();
                return;
            }

            if (this.settings.loading) {
                this.hideAllMeshes();
                this.showLoader(hitPoint);
            } else if (this.settings.hasActiveMarker) {
                this.mouseArrowObject.visible = false;

            } else if (this.eventManager.GetMouseButtonDown(0)) {
                this.hideAllMeshes();
            } else if (camera.position) {
                this.hideAllMeshes();
                this.showArrow();
                this.updateArrowPosition(camera, hitPoint);
            } else {
                this.hideAllMeshes();
            }
        }

    }

    private updateArrowPosition(camera: PerspectiveCamera | OrthographicCamera, hitPoint: Vector3) {
        const minDistance = 3;  // Minimum distance threshold for arrow movement
        const cameraPositionXZ = new Vector3(camera.position.x, 0, camera.position.z);  // Camera's XZ position
        const distanceToHitPoint = new Vector3(hitPoint.x, 0, hitPoint.z).distanceTo(cameraPositionXZ);  // Distance to hit point

        // Calculate direction vector from the camera to the hit point
        const direction = hitPoint.clone().sub(camera.position).normalize();
        const rotationY = Math.atan2(direction.x, direction.z) + Math.PI;  // Rotation angle for the arrow

        // If the distance is greater than the minimum threshold, move the arrow
        if (distanceToHitPoint > minDistance) {
            const movement = direction.multiplyScalar(minDistance);  // Limit the arrow's movement to the min distance
            this.mouseArrowObject.position.copy(camera.position).add(movement);  // Update arrow position
        }
        // Otherwise, place the arrow directly on the hit point
        else {
            this.mouseArrowObject.position.copy(hitPoint);
        }

        // Set the arrow's rotation based on the calculated direction
        this.mouseArrowObject.rotation.y = rotationY;
    }

    private hideAllMeshes() {
        this.mouseArrowObject.visible = false;
        this.loadingSpinnerObject.visible = false;
    }

    private showArrow() {
        this.mouseArrowObject.visible = true;
    }

    private showLoader(hitPoint: Vector3) {
        this.loadingSpinnerObject.visible = true;
        this.loadingSpinnerObject.position.copy(hitPoint);
    }

    update() {

        if (this.settings.loading && this.loadingSpinnerObject) {
            this.loadingSpinnerObject.rotation.y += 7 * this.timeService.deltaTime; // Multiplied to control rotation speed
        }

        this.renderer.setClearColor(0, 0);
        this.renderer.setRenderTarget(this.renderTexture);
        this.renderer.render(this.scene, this.cameraHandler.camera);
        this.renderer.setRenderTarget(null);
    }
}

